import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import {
  classifyRows,
  parseCsiuRows,
  readSpreadsheet,
  UnreadableSpreadsheet,
  type BudgetCode,
  type ClassifiedRow,
  type CsiuRowProblem,
  type ExistingTransaction,
  type ParsedCsiuRow,
} from '@budgetmonkey/shared'
import { useCurrentUser } from '@/auth/AuthProvider'
import { useResolvedFiscalYear } from '@/lib/fiscalYear'
import { useBudgetCodes } from '@/features/budgetCodes/hooks'
import { useTransactions } from '@/features/transactions/hooks'
import { commitCsiuImport, type ImportCommit, type RowDecision } from './api'

/** One dropped file, once it has been read. */
export interface LoadedFile {
  filename: string
  rows: ParsedCsiuRow[]
  /** Distinct account codes found — normally exactly one per CSIU export. */
  accountCodes: string[]
  /**
   * Rows that could not be read. Carried rather than discarded: a row whose
   * amount is unreadable used to vanish, so the review screen's counts
   * reconciled perfectly against a file that had one more line in it.
   */
  problems: CsiuRowProblem[]
  error?: string
}

/**
 * Read dropped CSIU exports and classify every row against the ledger.
 *
 * All files are classified together rather than one at a time. §6.4 routes
 * each row by its `AccountCode`, so file order and naming are irrelevant — and
 * duplicate detection has to see the whole import at once, or the same charge
 * appearing in two exports would be accepted twice.
 */
export function useCsiuImport(explicitYear?: number) {
  const fiscalYear = useResolvedFiscalYear(explicitYear)
  const user = useCurrentUser()
  const client = useQueryClient()
  const { data: codes = [] } = useBudgetCodes(fiscalYear)
  const { data: transactions = [] } = useTransactions(fiscalYear)

  const [files, setFiles] = useState<LoadedFile[]>([])
  const [classified, setClassified] = useState<ClassifiedRow[]>([])
  const [excluded, setExcluded] = useState<Set<number>>(new Set())

  const load = useCallback(
    async (incoming: readonly File[]) => {
      const loaded: LoadedFile[] = []
      // Row numbers must be unique across the whole import, since reversal
      // pairing and duplicate matching both key on them.
      let nextRowNumber = 2

      for (const file of incoming) {
        try {
          const bytes = new Uint8Array(await file.arrayBuffer())
          const table = readSpreadsheet(bytes, file.name)
          const { parsed: rows, problems } = parseCsiuRows(table.rows, nextRowNumber)
          // Every row consumes a number whether it parsed or not — reversal
          // pairing and duplicate matching key on it.
          nextRowNumber += table.rows.length

          loaded.push({
            filename: file.name,
            rows,
            problems,
            accountCodes: [
              ...new Set(rows.map((r) => r.accountCode).filter((c) => c !== null)),
            ],
          })
        } catch (cause) {
          loaded.push({
            filename: file.name,
            rows: [],
            problems: [],
            accountCodes: [],
            error:
              cause instanceof UnreadableSpreadsheet
                ? cause.message
                : `Couldn't read ${file.name}.`,
          })
        }
      }

      const existing: ExistingTransaction[] = transactions.map((t) => ({
        id: t.id ?? '',
        sourceRef: t.sourceRef,
        amount: t.amount,
        vendor: t.vendor,
        transactionDate: t.transactionDate,
        status: t.status,
      }))

      setFiles(loaded)
      setClassified(classifyRows(loaded.flatMap((f) => f.rows), existing))
      setExcluded(new Set())
    },
    [transactions],
  )

  const toggle = useCallback((rowNumber: number) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(rowNumber)) next.delete(rowNumber)
      else next.add(rowNumber)
      return next
    })
  }, [])

  const reset = useCallback(() => {
    setFiles([])
    setClassified([])
    setExcluded(new Set())
  }, [])

  const codesByAccountCode = new Map<string, BudgetCode>(
    codes.map((code) => [code.code, code]),
  )

  /** Account codes present in the files that have no budget code configured. */
  const unknownAccountCodes = [
    ...new Set(
      classified
        .map((c) => c.row.accountCode)
        .filter((code): code is string => code !== null && !codesByAccountCode.has(code)),
    ),
  ]

  const commit = useMutation({
    mutationFn: () => {
      const decisions: RowDecision[] = classified.map((c) => ({
        classified: c,
        include: !excluded.has(c.row.rowNumber),
      }))
      const input: ImportCommit = {
        decisions,
        codesByAccountCode,
        filenames: files.map((f) => f.filename),
        fiscalYear,
      }
      return commitCsiuImport(user.uid, input)
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['transactions', user.uid] })
      reset()
    },
  })

  return {
    files,
    classified,
    excluded,
    unknownAccountCodes,
    codesByAccountCode,
    load,
    toggle,
    reset,
    commit,
  }
}
